import React, { useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { addManualPurchase } from "../../../api/api";
import styles from "./addRacketPurchases.module.css";

const createEmptyProduct = () => ({
  id: Date.now() + Math.random(),
  product_id: "",
  product_name: "",
  quantity: "1",
});

const AddRacketPurchases = () => {
  const { customerId } = useParams();
  const navigate = useNavigate();
  const { state } = useLocation();
  const customerName = state?.customerName || "Customer";

  const [isSubmitting, setIsSubmitting] = useState(false);

  // Store information
  const [storeOrderNumber, setStoreOrderNumber] = useState("");
  const [receiptNumber, setReceiptNumber] = useState("");
  const [storeName, setStoreName] = useState("");
  
  // Products on this receipt
  const [products, setProducts] = useState([createEmptyProduct()]);
  
  // Single price for entire receipt
  const [totalPrice, setTotalPrice] = useState("");
  const [purchaseDate, setPurchaseDate] = useState("");
  
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  const updateProduct = (index, field, value) => {
    const updated = [...products];
    updated[index][field] = value;
    setProducts(updated);
  };

  const addProduct = () => setProducts([...products, createEmptyProduct()]);
  
  const removeProduct = (index) => {
    const updated = [...products];
    updated.splice(index, 1);
    setProducts(updated);
  };

  // Calculate total racket count and price per racket
  const getTotalRacketCount = () => {
    return products.reduce((sum, p) => sum + (parseInt(p.quantity) || 0), 0);
  };

  const getPricePerRacket = () => {
    const total = parseFloat(totalPrice) || 0;
    const count = getTotalRacketCount();
    return count > 0 ? (total / count).toFixed(2) : "0.00";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMsg("");
    setErr("");
    setIsSubmitting(true);

    try {
      // Send as single request with array of products
      await addManualPurchase(customerId, {
        products,
        total_price: totalPrice,
        purchase_date: purchaseDate,
        store_order_number: storeOrderNumber,
        receipt_number: receiptNumber,
        store_name: storeName,
      });

      setProducts([createEmptyProduct()]);
      setTotalPrice("");
      setPurchaseDate("");
      setStoreOrderNumber("");
      setReceiptNumber("");
      setStoreName("");
      setMsg("✅ Purchases saved successfully.");
    } catch (error) {
      const apiError =
        error?.response?.data?.message || "Failed to save purchases.";
      setErr(apiError);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h2>
          Add In-Store Purchases for <span>{customerName}</span>
        </h2>
      </div>

      {msg && <p className={styles.success}>{msg}</p>}
      {err && <p className={styles.error}>{err}</p>}

      <form onSubmit={handleSubmit} className={styles.form}>
        {/* Store Information */}
        <div className={styles.storeInfo}>
          <h3>Receipt Information</h3>
          <div className={styles.storeGrid}>
            <div>
              <label>
                Store Order Number <span className={styles.required}>*</span>
              </label>
              <input
                value={storeOrderNumber}
                onChange={(e) => setStoreOrderNumber(e.target.value)}
                type="text"
                required
              />
            </div>

            <div>
              <label>
                Receipt Number <span className={styles.required}>*</span>
              </label>
              <input
                value={receiptNumber}
                onChange={(e) => setReceiptNumber(e.target.value)}
                type="text"
                required
              />
            </div>

            <div>
              <label>
                Store Name <span className={styles.required}>*</span>
              </label>
              <select
                value={storeName}
                onChange={(e) => setStoreName(e.target.value)}
                required
              >
                <option value="">-- Select Store --</option>
                <option value="Marina Store">Marina Store</option>
                <option value="Dubai Hills Store">Dubai Hills Store</option>
                <option value="Jumeirah Store">Jumeirah Store</option>
              </select>
            </div>

            <div>
              <label>
                Purchase Date <span className={styles.required}>*</span>
              </label>
              <input
                value={purchaseDate}
                onChange={(e) => setPurchaseDate(e.target.value)}
                type="date"
                required
              />
              <span className={styles.helperText}>
                The date of the actual purchase (can be backdated)
              </span>
            </div>
          </div>
        </div>

        {/* Products Section */}
        <div className={styles.productsSection}>
          <h3>Products on Receipt</h3>
          <p className={styles.infoNote}>
            ℹ️ Add all racket products. The selected date will be used with the current time (for backdating purchases).
          </p>

          {products.map((product, idx) => (
            <div key={product.id} className={styles.productRow}>
              <div className={styles.rowHeader}>
                <h4>Product #{idx + 1}</h4>
                {products.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeProduct(idx)}
                    className={styles.removeBtn}
                  >
                    ✖ Remove
                  </button>
                )}
              </div>

              <div className={styles.productGrid}>
                <div>
                  <label>Product SKU <span className={styles.required}>*</span></label>
                  <input
                    value={product.product_id}
                    onChange={(e) =>
                      updateProduct(idx, "product_id", e.target.value)
                    }
                    type="text"
                    required
                  />
                </div>

                <div>
                  <label>Product Name <span className={styles.required}>*</span></label>
                  <input
                    value={product.product_name}
                    onChange={(e) =>
                      updateProduct(idx, "product_name", e.target.value)
                    }
                    required
                  />
                </div>

                <div>
                  <label>
                    Quantity <span className={styles.required}>*</span>
                  </label>
                  <input
                    value={product.quantity}
                    onChange={(e) =>
                      updateProduct(idx, "quantity", e.target.value)
                    }
                    type="number"
                    min="1"
                    placeholder="1"
                    required
                  />
                  <span className={styles.helperText}>
                    How many of this product?
                  </span>
                </div>
              </div>
            </div>
          ))}

          <button
            type="button"
            onClick={addProduct}
            className={styles.addProductBtn}
          >
            + Add Another Product
          </button>
        </div>

        {/* Total Price Section */}
        <div className={styles.pricingSection}>
          <h3>Receipt Total</h3>
          <div className={styles.priceInput}>
            <label>
              Total Receipt Amount (AED) <span className={styles.required}>*</span>
            </label>
            <input
              value={totalPrice}
              onChange={(e) => setTotalPrice(e.target.value)}
              type="number"
              step="0.01"
              min="0"
              placeholder="e.g., 2000.00"
              required
            />
          </div>

          {/* Price Calculation Display */}
          {totalPrice && getTotalRacketCount() > 0 && (
            <div className={styles.priceBreakdown}>
              <div className={styles.breakdownRow}>
                <span>Total Rackets:</span>
                <strong>{getTotalRacketCount()}</strong>
              </div>
              <div className={styles.breakdownRow}>
                <span>Receipt Total:</span>
                <strong>{parseFloat(totalPrice).toFixed(2)} AED</strong>
              </div>
              <div className={styles.breakdownRow}>
                <span>Price per Racket:</span>
                <strong>{getPricePerRacket()} AED</strong>
              </div>
              <div className={styles.breakdownRow}>
                <span>Free Services Eligibility:</span>
                {parseFloat(getPricePerRacket()) >= 1000 ? (
                  <span className={styles.eligible}>
                    ✅ Each racket gets 10 free services
                  </span>
                ) : (
                  <span className={styles.notEligible}>
                    ❌ Not eligible (per racket &lt; 1000 AED)
                  </span>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className={styles.actions}>
          <button
            type="submit"
            className={styles.submitBtn}
            disabled={isSubmitting}
          >
            {isSubmitting ? "Submitting..." : "Submit Purchase"}
          </button>

          <button
            type="button"
            onClick={() => navigate(-1)}
            className={styles.backButton}
          >
            ← Back
          </button>
        </div>
      </form>
    </div>
  );
};

export default AddRacketPurchases;
